/*
 * gmx-custommarx-search.js — name search + sort for the CustomMarx collection.
 *
 * Matching is the mockup's: lowercased names with straight and curly
 * apostrophes stripped. The page shows 48 cards, but the search covers the
 * whole collection: on the first keystroke it fetches the alternate template
 * `?view=cmx-index` (250 items per request, same sort) and builds a full list
 * of item nodes. Until that arrives the search filters the current page's
 * cards; if the fetch fails it stays page-local. Clearing the search restores
 * the page's original cards and the pager.
 *
 * The sort <select> lives in a GET form (works without JS); here a change
 * navigates with `sort_by` set and `page` removed.
 */
(function () {
  const root = document.querySelector("[data-gmx-custommarx-search]");
  if (!root) return;

  const input = root.querySelector("[data-search-input]");
  const clear = root.querySelector("[data-search-clear]");
  const count = root.querySelector("[data-search-count]");
  const sortSelect = root.querySelector("[data-sort-select]");
  const grid = document.querySelector("[data-search-grid]");
  const empty = document.querySelector("[data-search-empty]");
  const pager = document.querySelector("[data-search-pager]");
  const term = empty ? empty.querySelector("[data-search-term]") : null;
  const scope = empty ? empty.querySelector("[data-search-scope]") : null;
  const searchAll = empty ? empty.querySelector("[data-search-all]") : null;
  if (!input || !grid) return;

  const pageItems = Array.from(grid.querySelectorAll("[data-search-item]"));
  const total = root.dataset.total || String(pageItems.length);
  const searchUrl = root.dataset.searchUrl || "/search";
  const collectionUrl = root.dataset.collectionUrl || window.location.pathname;
  const sort = root.dataset.sort || "";

  const norm = (s) => s.toLowerCase().replace(/[’'`]/g, "");
  const nameOf = (el) => norm(el.dataset.name || "");

  let allItems = null; // full-collection nodes once the index has loaded
  let indexRequest = null;

  if (sortSelect) {
    sortSelect.addEventListener("change", function () {
      const url = new URL(window.location.href);
      url.searchParams.set("sort_by", sortSelect.value);
      url.searchParams.delete("page");
      window.location.href = url.toString();
    });
  }

  function loadIndex() {
    if (indexRequest) return indexRequest;
    const base = new URL(collectionUrl, window.location.origin);
    base.searchParams.set("view", "cmx-index");
    if (sort) base.searchParams.set("sort_by", sort);

    async function fetchPage(n) {
      base.searchParams.set("page", String(n));
      const res = await fetch(base.toString(), { credentials: "same-origin" });
      if (!res.ok) throw new Error("index " + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), "text/html");
      const holder = doc.querySelector("[data-cmx-index]");
      if (!holder) throw new Error("index missing");
      return {
        pages: parseInt(holder.dataset.pages, 10) || 1,
        nodes: Array.from(holder.querySelectorAll("[data-search-item]")),
      };
    }

    indexRequest = (async function () {
      const first = await fetchPage(1);
      let nodes = first.nodes;
      for (let n = 2; n <= first.pages; n += 1) {
        nodes = nodes.concat((await fetchPage(n)).nodes);
      }
      return nodes.map((el) => document.importNode(el, true));
    })()
      .then(function (nodes) {
        allItems = nodes;
        apply();
      })
      .catch(function () {
        allItems = null; // silent fallback: stay page-local
      });
    return indexRequest;
  }

  let showing = pageItems; // nodes currently mounted in the grid

  function mount(list) {
    if (showing === list) return;
    grid.replaceChildren.apply(grid, list);
    showing = list;
  }

  function apply() {
    const raw = input.value.trim();
    const q = norm(raw);
    const full = allItems !== null;
    let visible = 0;

    if (q === "") {
      mount(pageItems);
      pageItems.forEach((el) => (el.hidden = false));
      visible = pageItems.length;
    } else if (full) {
      const matches = allItems.filter((el) => nameOf(el).indexOf(q) !== -1);
      grid.replaceChildren.apply(grid, matches);
      showing = null; // force a remount of the page cards on clear
      visible = matches.length;
    } else {
      mount(pageItems);
      pageItems.forEach((el) => {
        const match = nameOf(el).indexOf(q) !== -1;
        el.hidden = !match;
        if (match) visible += 1;
      });
    }

    if (clear) clear.hidden = raw === "";
    if (count) {
      if (raw === "") count.textContent = total + " marks";
      else if (full) count.textContent = visible + " of " + allItems.length + " marks";
      else count.textContent = visible + " of " + pageItems.length + " on this page";
    }

    const none = visible === 0 && raw !== "";
    if (empty) {
      empty.hidden = !none;
      if (term) term.textContent = raw;
      if (scope) scope.hidden = full;
      if (searchAll) {
        searchAll.href = searchUrl + "?q=" + encodeURIComponent(raw) + "&type=product";
      }
    }
    grid.hidden = none;
    if (pager) pager.hidden = none || (full && raw !== "");
  }

  input.addEventListener("input", function () {
    if (input.value.trim() !== "") loadIndex();
    apply();
  });
  if (clear) {
    clear.addEventListener("click", function () {
      input.value = "";
      input.focus();
      apply();
    });
  }

  apply();
})();
